import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import CouponDashboardCard from "@/components/CouponDashboardCard";
import { Shield, Users, DollarSign, Megaphone, BarChart3, AlertTriangle, Handshake, Send, MailPlus, KeyRound, PauseCircle, BookOpen, Printer, BookText, Quote, Mic } from "lucide-react";

/**
 * Admin panel — landing tiles. Each tile links to a future admin sub-page.
 * The tiles that don't have functionality yet render disabled cards so
 * admins can see the roadmap without hitting dead links.
 */
const TILES = [
  { id: "distress", label: "Distress Events", icon: AlertTriangle, to: "/admin/distress", note: "Care log · 90d retention" },
  { id: "announcements", label: "Announcements", icon: Megaphone, to: "/admin/announcements", note: "Speak to all users" },
  { id: "analytics", label: "Analytics", icon: BarChart3, to: "/admin/analytics", note: "Traffic · usage · voices" },
  { id: "cancel-save", label: "Cancel-Save Analytics", icon: PauseCircle, to: "/admin/cancel-save", note: "Off-ramp funnel · save ratio" },
  { id: "founders", label: "Founders", icon: Users, to: "/admin/founders", note: "First 100 seats · roster + status" },
  { id: "affiliates", label: "Affiliates", icon: Handshake, to: "/admin/affiliates", note: "Peer referrals + paid partners" },
  { id: "ebooks", label: "Ebook Library", icon: BookOpen, to: "/admin/ebooks", note: "Companion drops · upload PDFs" },
  { id: "site", label: "Site Trust", icon: Mic, to: "/admin/site", note: "Founder voice note · review badge" },
  { id: "stories", label: "Listener Stories", icon: Quote, to: "/admin/stories", note: "Quotes · invites · hidden until Live" },
  { id: "blog", label: "Blog", icon: BookText, to: "/admin/blog", note: "SEO essays · sitemap · RSS" },
  { id: "pricing", label: "Stripe Pricing", icon: DollarSign, to: "/admin/pricing", note: "Live · type-to-confirm" },
  { id: "broadcasts", label: "Broadcasts", icon: Send, to: "/admin/broadcasts", note: "One-time email campaigns" },
  { id: "marketing", label: "Marketing List", icon: MailPlus, to: "/admin/marketing", note: "Non-paid subscribers · CSV export" },
  { id: "providers", label: "API Providers", icon: KeyRound, to: "/admin/providers", note: "Balance · keys · quick top-up" },
  { id: "promo-card", label: "Promo Card", icon: Printer, to: "/promo-card", note: "Printable QR handout · 3.5×5" },
];

export default function Admin() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <div className="flex items-center gap-3">
          <Shield className="h-4 w-4 text-terracotta" />
          <span className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Umbra · Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-4">Overview</p>
        <h1 className="font-serif text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          Welcome, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="font-serif italic text-lg text-bone-400 max-w-2xl leading-relaxed mb-16">
          The workbook, from the other side. Choose a tile to manage.
        </p>

        <CouponDashboardCard />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-tiles">
          {TILES.map((tile) => {
            const Icon = tile.icon;
            const disabled = !tile.to;
            const content = (
              <div
                data-testid={`admin-tile-${tile.id}`}
                className={`border border-obsidian-600 p-6 h-full transition-colors ${
                  disabled ? "opacity-50" : "hover:border-terracotta cursor-pointer"
                }`}
              >
                <Icon className="h-6 w-6 text-terracotta mb-4" />
                <p className="font-serif text-xl text-bone-100 mb-2">{tile.label}</p>
                {tile.note && (
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500">
                    {tile.note}
                  </p>
                )}
              </div>
            );
            return disabled ? (
              <div key={tile.id}>{content}</div>
            ) : (
              <Link key={tile.id} to={tile.to}>{content}</Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
